import { translate } from '@util/Localization';
import { isDirectToPayRoute } from '@util/RouterUtils';
import React, { FunctionComponent, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { capitalise, isDefined, isNonEmptyString } from 'ts-utils';
import { chooseAddonPlanPerExperiment } from '../../../businessLogic/AddonPlans';
import { PROGRAM_LENGTH } from '../../../businessLogic/PlansData';
import { getHcpDetailsFromUrl } from '../../../config/AssessmentUtils';
import { Color } from '../../../config/Color';
import { useAppStore } from '../../../store/Store';
import * as images from '../../assets/Images';
import * as logos from '../../assets/images/Logos';
import { Body, bodyFontStyle } from '../../styles/Typography';
import { PricingInfoContainer } from '../CheckoutInfo/CheckoutInfo';
import { PlanSummaryTableForm } from '../CheckoutInfo/PlanSummaryTableForm';
import { ProductAddon } from '../ProductAddon';
import { ComboDiscount } from './ComboTrust';
import { HeldCountdown } from './HeldCountdown';
import { listForRow, programAnswers, ProgramUserData } from './ProgramAnswers';

type SoftTrialComboProps = {
  /** Control's email or Continue step and card form, shown below Control's price summary. */
  purchaseForm: ReactNode;
};

/**
 * The `PaymentSoftTrial` Combo page: the trial as theirs (countdown, their name and condition, the
 * doctor, what's included and the guarantee), then Control's price summary and checkout.
 */
export const SoftTrialCombo: FunctionComponent<SoftTrialComboProps> = (props) => {
  const addon = chooseAddonPlanPerExperiment();

  return (
    <>
      {/* Control's countdown sends people back to their results at zero, so /start doesn't show it. */}
      {!isDirectToPayRoute() && (
        <StickyCountdown>
          <HeldCountdown />
        </StickyCountdown>
      )}
      <Page data-test="softTrialCombo">
        <TrialHeading />
        <ProgramCard />
        <Included />
        <GuaranteeCard />
      </Page>
      {/* Two containers, as in Control, so the form sits the same distance below the prices. */}
      <PricingInfoContainer>
        {addon !== undefined && <ProductAddon product={addon} />}
        <PlanSummaryTableForm discount={<ComboDiscount />} />
      </PricingInfoContainer>
      <PurchaseForm>{props.purchaseForm}</PurchaseForm>
    </>
  );
};

/** "Alex, your free 7-day trial is ready". */
const TrialHeading: FunctionComponent = () => {
  const { name, trialPeriodDays } = useAppStore((state) => ({
    name: state.user.name,
    trialPeriodDays: state.purchase.trialPeriodDays,
  }));
  const trial = `free ${trialPeriodDays}-day trial is ready`;

  return (
    <Heading>
      {isNonEmptyString(name) ? `${capitalise(name)}, your ${trial}` : `Your ${trial}`}
    </Heading>
  );
};

/** Who referred them, their condition from the quiz, the program, and who leads it. */
const ProgramCard: FunctionComponent = () => {
  const referralHcpDetails = getHcpDetailsFromUrl();
  const userData = useAppStore((state) => state.userData);
  const answers = programAnswers(userData as ProgramUserData);
  // The subtype says the most, e.g. "IBS-D (diarrhea)"; without one, the conditions they named.
  const condition =
    answers.subtypes.length > 0 ? answers.subtypes.join(' · ') : listForRow(answers.conditions);

  return (
    <Card>
      {isDefined(referralHcpDetails) && <ReferrerRow {...referralHcpDetails} />}
      {isNonEmptyString(condition) && <DetailRow label="Tailored for">{condition}</DetailRow>}
      <DetailRow label="Program">{`${PROGRAM_LENGTH.weeks} weeks + ongoing support`}</DetailRow>
      <DoctorRow>
        <Avatar src={images.simonePeters2024} alt="" width={56} height={56} />
        <div>
          <DoctorName>{translate('doctorShort')}</DoctorName>
          <Logos>
            <img src={logos.monashReverse} alt="Monash University" />
            <img src={logos.mindGut} alt="Mind + Gut Clinic" />
          </Logos>
        </div>
      </DoctorRow>
    </Card>
  );
};

/** Control's promises, as what the trial includes. */
const Included: FunctionComponent = () => (
  <Section>
    <SectionHeading>Included in your trial</SectionHeading>
    <Benefits>
      {translate('payment.productDetails')
        .split('\n')
        .map((item) => (
          <Benefit key={item}>
            <TickBadge aria-hidden>
              <svg viewBox="0 0 12 12" width={12} height={12}>
                <path
                  d="M2.5 6.2 5 8.5 9.5 3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </TickBadge>
            <BenefitText>{item}</BenefitText>
          </Benefit>
        ))}
    </Benefits>
  </Section>
);

/** The clinician who referred them, from the referral link, title-cased as the link may not be. */
const ReferrerRow: FunctionComponent<{ hcp: string; hcpClinic: string }> = (props) => (
  <DetailRow label="Referrer">
    <div data-test="referralClinician">
      {capitalise(props.hcp.trim().toLowerCase())}
      <RowDetail>{capitalise(props.hcpClinic.trim().toLowerCase())}</RowDetail>
    </div>
  </DetailRow>
);

/** Control's guarantee text, word for word, so a policy change reaches this page too. */
const GuaranteeCard: FunctionComponent = () => (
  <GuaranteeBox data-test="moneyBackGuarantee">
    <CardTitle>Money back guarantee</CardTitle>
    <CardBody>{translate('payment.guarantee.aiPersonalizedInfo.info')}</CardBody>
  </GuaranteeBox>
);

const DetailRow: FunctionComponent<{ label: string; children: ReactNode }> = (props) => (
  <Row>
    <RowLabel>{props.label}</RowLabel>
    <RowValue>{props.children}</RowValue>
  </Row>
);

/** Outside `Page`, so it stays pinned while the whole page scrolls, checkout included. */
const StickyCountdown = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: ${({ theme }) => `${theme.spacing._8px} ${theme.spacing._16px}`};
`;

const PurchaseForm = styled(PricingInfoContainer)`
  /* antd shadows primary-button text, which blurs the label. */
  .ant-btn-primary {
    text-shadow: none;
  }

  /* "No commitment — cancel any time", centred under the button with its tick. */
  [data-test='collectCardForm'] > ul {
    margin: ${({ theme }) => `${theme.spacing._20px} 0 0`};
    padding: 0;
    list-style-position: inside;
    text-align: center;
  }
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  /* Lines up with Control's price summary and form below. */
  padding: ${({ theme }) => `${theme.spacing._4px} ${theme.spacing._16px} ${theme.spacing._16px}`};
  text-align: left;
`;

const Text = styled(Body)`
  margin: 0;
`;

const Muted = styled(Text)`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  color: ${Color.content.muted};
`;

// Real headings (h1 for the page, h2 per section) so screen readers can navigate the page.
const headingStyle = css`
  ${bodyFontStyle}
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  letter-spacing: -0.019em;
`;

const Heading = styled.h1`
  ${headingStyle}
  font-size: 32px;
  line-height: 1.15;
`;

const SectionHeading = styled.h2`
  ${headingStyle}
  font-size: 19px;
  line-height: 1.2;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._16px};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing._4px} ${theme.spacing._20px} ${theme.spacing._20px}`};
  border-radius: 20px;
  background: ${Color.surface.onAccent};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
  padding: ${({ theme }) => `${theme.spacing._16px} 0`};
  border-bottom: 0.6px solid ${Color.decorative.default};
`;

const RowLabel = styled(Text)`
  /* Long values wrap on the right, so the label stays on one line. */
  flex-shrink: 0;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  color: ${Color.content.muted};
`;

const RowValue = styled.div`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-align: right;
  color: ${Color.content.default};
`;

const RowDetail = styled(Muted)`
  margin-top: 2px;
  font-size: 13px;
`;

const DoctorRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._16px};
  padding-top: ${({ theme }) => theme.spacing._18px};
`;

const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
`;

const DoctorName = styled(Text)`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
  margin-top: ${({ theme }) => theme.spacing._10px};

  img {
    display: block;
    height: 20px;
    width: auto;
  }
  /* Mind + Gut's file is in colour with padding around it: white, and a little taller to match. */
  img + img {
    height: 24px;
    filter: brightness(0) invert(1);
  }
`;

const Benefits = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._12px};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Benefit = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._14px};
`;

const TickBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${Color.action.default};
  color: ${Color.content.default};
`;

const BenefitText = styled(Text)`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1.4;
`;

const GuaranteeBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._6px};
  padding: ${({ theme }) => theme.spacing._20px};
  border-radius: 20px;
  background: ${Color.surface.onAccent};
  text-align: center;
`;

const CardTitle = styled(Text)`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const CardBody = styled(Text)`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.light};
  line-height: 1.45;
  color: ${Color.content.muted};
`;
